<template>
  <mastery-footer
    class="deck-page-footer"
    :primary-action-text="primaryActionText"
    :secondary-action-text="secondaryActionText"
    @primary-action="$emit('go-to-previous', previousCard)"
    @secondary-action="$emit('go-to-next', nextCard)"
  >
    <template #before-action>
      <button
        class="more-info-button"
        :class="{ open: showMoreInfo }"
        @click="toggleMoreInfo"
      >
        <icon
          class="dots-icon"
          name="dots"
        />
      </button>
      <div
        v-if="showMoreInfo"
        class="more-info"
      >
        <p class="title option">
          Improve the app
        </p>
        <a
          v-if="!!suggestATopicUrl"
          :href="suggestATopicUrl"
          target="_blank"
          rel="noopener"
          class="suggest option"
        >
          Suggest a related topic
        </a>
      </div>
      <bookmark-button
        v-if="showBookmark && bookmarkAsset"
        :asset-name="bookmarkAssetName"
        :bookmarkable-type="bookmarkableType"
        :bookmarkable-id="bookmarkableId"
      />
    </template>
    <template
      v-if="nextAsset"
      #after-action
    >
      <div class="upcoming-info">
        <p>Next</p> &rarr; {{ nextLabel }}
      </div>
    </template>
  </mastery-footer>
</template>

<script>
import Icon from '@components/singletons/icon.vue';
import BookmarkButton from '@components/singletons/bookmarkButton.vue';
import MasteryFooter from '@components/mastery/footer.vue';
import CardMixin from '@/mixins/card';
import GlobalMixin from '@/mixins/global';

export default {
  components: { Icon, BookmarkButton, MasteryFooter },
  mixins: [CardMixin, GlobalMixin],
  props: {
    card: {
      type: Object,
    },
    deck: {
      type: Object,
    },
    showBookmark: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['go-to-previous', 'go-to-next'],
  data() {
    return {
      showMoreInfo: false,
    };
  },
  computed: {
    bookmarkAsset() {
      if (this.card) {
        return this.cardAsset(this.card);
      }
      return null;
    },
    bookmarkAssetName() {
      return this.bookmarkAsset?.assetTitle || this.bookmarkAsset?.title;
    },
    bookmarkableId() {
      return this.card?.assetId;
    },
    bookmarkableType() {
      return this.card?.assetType;
    },
    currentCardIndex() {
      const currentCardId = this.card?.id;
      return this.orderedCards.findIndex(card => card.id === currentCardId);
    },
    nextAsset() {
      if (this.card && this.nextCard) {
        return this.cardAsset(this.nextCard);
      }
    },
    nextLabel() {
      if (this.nextCard?.isTopicCard && this.nextCard.title) {
        return this.nextCard.title;
      }
      return this.nextAsset?.assetTitle;
    },
    nextCard() {
      if ((this.currentCardIndex + 1) <= this.orderedCards.length) {
        return this.orderedCards[this.currentCardIndex + 1];
      }
    },
    orderedCards() {
      if (this.deck?.cards) {
        return [...this.deck.cards].sort((a, b) => a.order - b.order);
      }
    },
    previousAsset() {
      if (this.card && this.previousCard) {
        return this.cardAsset(this.previousCard);
      }
    },
    previousCard() {
      if ((this.currentCardIndex - 1) >= 0) {
        return this.orderedCards[this.currentCardIndex - 1];
      }
    },
    primaryActionText() {
      if (this.card && this.previousAsset) {
        return 'Previous';
      }
    },
    secondaryActionText() {
      if (this.card && this.nextAsset) {
        return 'Next';
      }
    },
    suggestATopicUrl() {
      const pv = this.$store.getters['productVars/get']['suggestURL'];
      return pv?.value;
    },
  },
  methods: {
    toggleMoreInfo() {
      this.showMoreInfo = !this.showMoreInfo;
    },
  },
};
</script>

<style lang="scss" scoped>
.deck-page-footer {
  align-items: center;
  flex-direction: row;

  .more-info-button {
    background: transparent;
    border-radius: 8px;
    height: rem-calc(24);
    margin: 0 8px;
    padding: 3px;
    width: rem-calc(24);

    &.open {
      background-color: #e4e4e4;
    }
  }

  &.mastery {
    left: rem-calc(88);

    .more-info-button {
      margin-left:48px;
    }
  }

  .upcoming-info,
  :deep(.upcoming-info) {
    align-items: center;
    display: flex;
    color: #a2a2a5;
    font-size: rem-calc(14);
    margin: 0 rem-calc(48) 0 auto;
  }

  .upcoming-info {
    @include breakpoint(small) {
      display: none;
    }
  }

  @include breakpoint(small) {
    left: 0;
    padding: 0 rem-calc(16);

    &.mastery.mastery {
      left: 0;
      bottom: rem-calc(0);

      .more-info-button {
        margin-left: 16px;
      }
    }
  }

  .more-info {
    @include shadowed;
    background: #ffffff;
    border-radius: 8px;
    left: 16px;
    position: absolute;
    text-align: center;
    bottom: 60px;
    width: rem-calc(232);

    .option {
      background: transparent;
      border-bottom: 1px solid #e4e4e4;
      color: inherit;
      display: block;
      margin: 0;
      padding: 12px 0;
      text-decoration: none;
      width: 100%;

      &:last-child {
        border-color: transparent;
      }
    }

    .title {
      color: #767676;
      font-size: rem-calc(12);
      font-weight: $bold;
    }

    .improve {
      color: #dc3416;
    }
  }
}
</style>
